'use client'

import { useEffect, useMemo, useState } from 'react'

import translateText from 'app/lib/translatedText'
import { cn } from 'app/lib/utils'
import Image from 'next/image'
import { Button } from 'components/ui/button'
import { PolygonWidgetWrapper } from 'components/WidgetWrapper'

interface DynamicContentWidgetProps {
    lang: string
    data?: IDynamicContentWidget
    config?: IDomainConfig
    styles?: IWidgetStyles
    styleType?: WidgetStyleType
}

const normalizeProfiles = (profiles: IDynamicContentProfile[] | undefined, lang: string) => {
    // Only process profiles if they exist and are valid
    if (!Array.isArray(profiles) || profiles.length === 0) {
        return []
    }

    return profiles
        .map((profile, index) => {
            if (!profile) return null

            const title = profile?.title ? translateText(lang, profile.title) : ''
            const normalizedTitle =
                title && title !== '<ERROR>' ? title : translateText('en', profile.title || {}) || ''

            // Only use steps if they exist - no fallback
            const normalizedSteps =
                Array.isArray(profile?.steps) && profile.steps.length > 0
                    ? (profile.steps
                          .map(step => {
                              if (!step) return null

                              const stepTitle = step?.title ? translateText(lang, step.title) : ''
                              const safeTitle =
                                  stepTitle && stepTitle !== '<ERROR>'
                                      ? stepTitle
                                      : translateText('en', step?.title || {}) || ''

                              const stepDescription = step?.description ? translateText(lang, step.description) : ''
                              const safeDescription =
                                  stepDescription && stepDescription !== '<ERROR>'
                                      ? stepDescription
                                      : translateText('en', step?.description || {}) || ''

                              if (!safeTitle && !safeDescription) return null

                              return {
                                  title: safeTitle,
                                  description: safeDescription
                              }
                          })
                          .filter(Boolean) ?? [])
                    : []

            // Profile must have a title to be valid
            if (!normalizedTitle) return null

            return {
                id: profile?.id || profile?.type || `profile-${index}`,
                type: profile?.type || `profile-${index}`,
                title: normalizedTitle,
                buttonLabel: normalizedTitle,
                image: profile?.image || '',
                steps: normalizedSteps
            }
        })
        .filter(Boolean) as Array<{
        id: string
        type: string
        title: string
        buttonLabel: string
        image: string
        steps: Array<{ title: string; description: string }>
    }>
}

const DynamicContentWidget = ({ lang, data, styleType: styleTypeProp }: DynamicContentWidgetProps) => {
    const profiles = useMemo(() => normalizeProfiles(data?.profiles, lang), [data?.profiles, lang])

    const [selectedType, setSelectedType] = useState<string>(() => {
        const preferred = data?.defaultProfileType
        if (preferred && profiles.some(profile => profile.type === preferred)) return preferred
        return profiles[0]?.type || ''
    })

    useEffect(() => {
        if (selectedType && profiles.some(profile => profile.type === selectedType)) return
        const preferred = data?.defaultProfileType
        if (preferred && profiles.some(profile => profile.type === preferred)) {
            setSelectedType(preferred)
            return
        }
        if (profiles[0]?.type) setSelectedType(profiles[0].type)
    }, [profiles, data?.defaultProfileType, selectedType])

    const activeProfile =
        profiles.find(profile => profile.type === selectedType) || profiles.find(profile => !!profile) || null

    const widgetStyleType: WidgetStyleType =
        (styleTypeProp as WidgetStyleType) ||
        ((data as IDynamicContentWidget & { styleType?: WidgetStyleType })?.styleType as WidgetStyleType) ||
        'default'

    const heading = useMemo(() => {
        if (!data?.heading) return ''
        const translated = translateText(lang, data.heading)
        if (translated && translated !== '<ERROR>') return translated
        return ''
    }, [data?.heading, lang])

    const description = useMemo(() => {
        if (!data?.description) return ''
        const translated = translateText(lang, data.description)
        if (translated && translated !== '<ERROR>') return translated
        return ''
    }, [data?.description, lang])

    // Don't render if no valid data from backend
    if (!data || !activeProfile || profiles.length === 0) return null

    // Polygon style rendering
    if (widgetStyleType === 'polygon_style') {
        return (
            <PolygonWidgetWrapper>
                <div className='grid grid-cols-1 custom:grid-cols-2 gap-8 custom:gap-4 mb-10 extra_large:mb-16 px-4 md:px-0 extra_large:gap-16'>
                    <div className='grid gap-y-6 order-2 custom:order-1'>
                        <div className='relative h-[300px] custom:h-[420px] extra_large:h-[700px] w-full grid grid-cols-2 gap-4 mx-auto custom:mr-auto'>
                            {profiles.map(profile => {
                                const isActive = profile.type === activeProfile.type
                                return (
                                    <div
                                        key={profile.id}
                                        onClick={() => setSelectedType(profile.type)}
                                        className='relative'
                                    >
                                        {/* SVG polygon background + border */}
                                        {isActive && (
                                            <svg
                                                className='absolute inset-0 w-full h-full -z-10 pointer-events-none'
                                                viewBox='0 0 100 100'
                                                preserveAspectRatio='none'
                                                xmlns='http://www.w3.org/2000/svg'
                                                aria-hidden='true'
                                            >
                                                {/* faded background */}
                                                <polygon
                                                    points='6,0 100,0 100,94 94,100 0,100 0,6'
                                                    fill='currentColor'
                                                    fillOpacity='0.06'
                                                />
                                                {/* border */}
                                                <polygon
                                                    points='6,0 100,0 100,94 94,100 0,100 0,6'
                                                    fill='none'
                                                    stroke='currentColor'
                                                    strokeWidth='1'
                                                    strokeLinejoin='bevel'
                                                    vectorEffect='non-scaling-stroke'
                                                />
                                            </svg>
                                        )}

                                        {/* Card content, clipped to same polygon */}
                                        <div className='[clip-path:polygon(4%_0,100%_0,100%_96%,96%_100%,0_100%,0_4%)] h-full w-full flex flex-col'>
                                            <div className='relative h-[90%]'>
                                                {profile.image && (
                                                    <Image
                                                        src={profile.image}
                                                        alt='profile type'
                                                        fill
                                                        className='object-contain'
                                                        sizes="(max-width: 768px) 100vw, 33vw"
                                                    />
                                                )}
                                            </div>
                                            <button
                                                className={cn(
                                                    `relative h-[10%] w-full font-semibold  polygon_shape`,
                                                    isActive ? 'text-black bg-accent-foreground' : 'text-foreground'
                                                )}
                                            >
                                                {profile.title}
                                            </button>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                        {(heading || description) && (
                            <div className='grid gap-y-3 text-center custom:text-left w-[95%]'>
                                {heading && (
                                    <p className='text-2xl custom:text-4xl extra_large:tex-5xl font-extrabold text-foreground leading-normal'>
                                        {heading}
                                    </p>
                                )}
                                {description && (
                                    <p className='text-sm custom:text-base extra_large:text-xl max-w-xl custom:max-w-none mx-auto custom:mx-0'>
                                        {description}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                    <div className='grid gap-y-6 order-1 custom:order-2'>
                        {activeProfile.steps.map((step, index) => (
                            <div
                                key={index}
                                className=' [clip-path:polygon(4%_0,100%_0,100%_88%,96%_100%,0_100%,0_11%)] bg-accent-foreground/5 rounded-bl-sm rounded-tr-sm  grid gap-3 custom:gap-4 font-manrope object-contain w-full
        overflow-hidden p-4 custom:p-8 py-6 custom:py-10 relative'
                            >
                                <p className='text-xl custom:text-3xl extra_large:text-4xl font-extrabold text-foreground leading-normal'>
                                    {step.title}
                                </p>
                                <p className='text-sm custom:text-base extra_large:text-xl'>{step.description}</p>
                                <p className='absolute top-4 custom:top-6 right-4 custom:right-6 opacity-20 text-xl custom:text-3xl extra_large:text-4xl font-bold'>
                                    {index + 1}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </PolygonWidgetWrapper>
        )
    } else if (widgetStyleType === 'default' || !widgetStyleType) {
        // Standard style rendering
        return (
            <div className='px-4 md:p-8'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-10 max-w-6xl mx-auto'>
                    <div className='grid gap-y-4 order-2 md:order-1'>
                        <div className='relative h-[280px] md:h-[350px] w-full grid grid-cols-2 gap-3 mx-auto md:mr-auto'>
                            {profiles.map(profile => {
                                const isActive = profile.type === activeProfile.type
                                return (
                                    <div
                                        key={profile.id}
                                        onClick={() => setSelectedType(profile.type)}
                                        className='relative cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-accent-foreground rounded-lg'
                                        role='button'
                                        tabIndex={0}
                                        aria-pressed={isActive}
                                        aria-label={`Select ${profile.buttonLabel}`}
                                    >
                                        <div
                                            className={cn(
                                                'flex h-full w-full flex-col overflow-hidden rounded-lg border border-border bg-background transition-shadow',
                                                isActive ? 'border-accent-foreground shadow-lg' : 'hover:shadow-md'
                                            )}
                                        >
                                            <div className='relative h-[90%] bg-muted/10'>
                                                {profile.image ? (
                                                    <Image
                                                        src={profile.image}
                                                        alt={profile.title || 'profile type'}
                                                        fill
                                                        className='object-cover'
                                                    />
                                                ) : (
                                                    <div className='flex h-full items-center justify-center bg-muted text-muted-foreground text-2xl font-bold'>
                                                        {profile.title?.[0]}
                                                    </div>
                                                )}
                                            </div>
                                            <Button
                                                type='button'
                                                variant={isActive ? 'default' : 'outline'}
                                                className={cn(
                                                    'h-[10%] w-full rounded-none font-semibold text-sm md:text-base',
                                                    isActive
                                                        ? 'bg-accent-foreground text-black hover:bg-accent-foreground/90'
                                                        : ''
                                                )}
                                                title={`Select ${profile.buttonLabel}`}
                                                aria-label={`Select ${profile.buttonLabel}`}
                                            >
                                                {profile.buttonLabel}
                                            </Button>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                        {(heading || description) && (
                            <div className='grid gap-y-2 text-center md:text-left w-[95%]'>
                                {heading && (
                                    <p className='text-xl md:text-2xl lg:text-3xl font-extrabold text-foreground leading-normal'>
                                        {heading}
                                    </p>
                                )}
                                {description && (
                                    <p className='text-sm md:text-base text-muted-foreground max-w-xl md:max-w-none mx-auto md:mx-0'>
                                        {description}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                    <div className='grid gap-y-4 order-1 md:order-2'>
                        {activeProfile.steps.map((step, index) => (
                            <div
                                key={`${activeProfile.id}-${index}`}
                                className='relative grid gap-2 p-4 md:p-6 rounded-lg border border-border bg-background shadow-sm'
                            >
                                {step.title && (
                                    <p className='text-lg md:text-xl lg:text-2xl font-extrabold text-foreground leading-normal'>
                                        {step.title}
                                    </p>
                                )}
                                {step.description && (
                                    <p className='text-sm md:text-base text-muted-foreground'>{step.description}</p>
                                )}
                                <p className='absolute top-4 right-4 opacity-20 text-xl md:text-2xl font-bold'>
                                    {index + 1}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )
    } else {
        // Widget type not available
        return (
            <div className='px-4 md:p-8'>
                <div className='relative w-full min-h-[400px] flex items-center justify-center'>
                    <div className='text-center space-y-4'>
                        <h2 className='text-2xl font-bold text-foreground'>Widget Type Not Available</h2>
                        <p className='text-muted-foreground'>
                            The requested dynamic content widget style type is not supported.
                        </p>
                    </div>
                </div>
            </div>
        )
    }
}

export default DynamicContentWidget
